import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, Col, Form, Row, Alert, Spinner } from "react-bootstrap";
import bg2 from "../assets/img/bg02.jpg";
import logo from "../assets/img/logo.jpeg";

const styles = `
  .reset-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  }
  
  .reset-card {
    border: none;
    border-radius: 20px;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
  }
  
  .reset-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 30px;
    text-align: center;
    color: white;
  }
  
  .reset-body {
    padding: 40px;
  }
  
  .password-input-wrapper {
    position: relative;
  }
  
  .password-input {
    padding-right: 45px !important;
  }
  
  .password-toggle-btn {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 45px;
    background: transparent;
    border: none;
    color: #6c757d;
    cursor: pointer;
  }
  
  .btn-gradient {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    padding: 12px;
    font-weight: 600;
    color: white;
    transition: all 0.3s ease;
  }
  
  .btn-gradient:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
  
  .strength-bar {
    height: 4px;
    transition: all 0.3s ease;
    border-radius: 2px;
  }
  
  .strength-weak { background: #dc3545; width: 33%; }
  .strength-medium { background: #ffc107; width: 66%; }
  .strength-strong { background: #28a745; width: 100%; }
`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  useEffect(() => {
    if (!token) {
      setError("Lien de réinitialisation invalide");
    }
  }, [token]);

  const checkPasswordStrength = (pass) => {
    let strength = 0;
    if (pass.length >= 8) strength++;
    if (pass.match(/[a-z]/) && pass.match(/[A-Z]/)) strength++;
    if (pass.match(/[0-9]/)) strength++;
    if (pass.match(/[^a-zA-Z0-9]/)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    checkPasswordStrength(newPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      setError("Lien de réinitialisation invalide");
      return;
    }

    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        'https://josephsbusiness.com/api/reset_password.php',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, password })
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => navigate("/pages/signin2"), 5000);
      } else {
        setError(data.message || "Une erreur est survenue");
      }
    } catch (err) {
      setError("Erreur de connexion au serveur");
    } finally {
      setLoading(false);
    }
  };

  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return "Faible";
    if (passwordStrength === 2) return "Moyen";
    if (passwordStrength >= 3) return "Fort";
    return "";
  };

  const getStrengthClass = () => {
    if (passwordStrength <= 1) return "strength-weak";
    if (passwordStrength === 2) return "strength-medium";
    if (passwordStrength >= 3) return "strength-strong";
    return "";
  };

  return (
    <>
      <style>{styles}</style>
      <div className="reset-container">
        <div className="container">
          <Row className="justify-content-center">
            <Col md={8} lg={6}>
              <Card className="reset-card">
                <div className="reset-header">
                  <img 
                    src={logo} 
                    style={{ 
                      height: "60px", 
                      width: "60px",
                      borderRadius: "50%", 
                      marginBottom: "15px",
                      border: "3px solid white"
                    }} 
                    alt="Logo" 
                  />
                  <h3>Réinitialiser le mot de passe</h3>
                  <p className="mb-0">Choisissez un nouveau mot de passe sécurisé</p>
                </div>
                
                <div className="reset-body">
                  {error && (
                    <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4">
                      <i className="ri-error-warning-line me-2"></i>
                      {error}
                    </Alert>
                  )}
                  
                  {success && (
                    <Alert variant="success" className="mb-4">
                      <i className="ri-checkbox-circle-line me-2"></i>
                      Mot de passe modifié avec succès !
                      <br />
                      <small>Redirection vers la page de connexion dans 5 secondes...</small>
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Nouveau mot de passe</Form.Label>
                      <div className="password-input-wrapper">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={password}
                          onChange={handlePasswordChange}
                          disabled={loading || success}
                          size="lg"
                          className="password-input"
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                      {password && (
                        <div className="mt-2">
                          <div className="d-flex justify-content-between mb-1">
                            <small>Force du mot de passe: {getStrengthLabel()}</small>
                          </div>
                          <div className={`strength-bar ${getStrengthClass()}`}></div>
                        </div>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Confirmer le mot de passe</Form.Label>
                      <div className="password-input-wrapper">
                        <Form.Control
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          disabled={loading || success}
                          size="lg"
                          className="password-input"
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          <i className={showConfirmPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                        </button>
                      </div>
                    </Form.Group>

                    <div className="d-grid gap-3">
                      <Button 
                        type="submit" 
                        className="btn-gradient"
                        disabled={loading || success || !token}
                        size="lg"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Réinitialisation en cours...
                          </>
                        ) : (
                          "Réinitialiser le mot de passe"
                        )}
                      </Button>
                      
                      <Button 
                        variant="link" 
                        onClick={() => navigate("/pages/signin2")}
                        className="text-decoration-none"
                      >
                        <i className="ri-arrow-left-line me-2"></i>
                        Retour à la connexion
                      </Button>
                    </div>
                  </Form>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}